import type { Layout } from './layout';
import type { ButtonId, EngineState } from '../types';

import aboutUrl from '../assets/buttons/about.png?url';
import linearUrl from '../assets/buttons/linear.png?url';
import randomUrl from '../assets/buttons/random.png?url';
import loopUrl from '../assets/buttons/loop.png?url';
import noLoopUrl from '../assets/buttons/noLoop.png?url';
import tempoLoUrl from '../assets/buttons/tempoLo.png?url';
import tempoHiUrl from '../assets/buttons/tempoHi.png?url';

export interface ButtonImages {
  about: HTMLImageElement;
  linear: HTMLImageElement;
  random: HTMLImageElement;
  loop: HTMLImageElement;
  noLoop: HTMLImageElement;
  tempoLo: HTMLImageElement;
  tempoHi: HTMLImageElement;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = src;
  });
}

export async function loadButtonImages(): Promise<ButtonImages> {
  const [about, linear, random, loop, noLoop, tempoLo, tempoHi] = await Promise.all([
    loadImage(aboutUrl),
    loadImage(linearUrl),
    loadImage(randomUrl),
    loadImage(loopUrl),
    loadImage(noLoopUrl),
    loadImage(tempoLoUrl),
    loadImage(tempoHiUrl),
  ]);
  return { about, linear, random, loop, noLoop, tempoLo, tempoHi };
}

interface ButtonRect {
  id: ButtonId;
  x: number;
  y: number;
  size: number;
}

function buttonRects(layout: Layout): ButtonRect[] {
  const { cssWidth, cssHeight, buttonPx, buttonInset } = layout;
  const size = buttonPx;
  return [
    { id: 'linear', x: buttonInset, y: buttonInset, size },
    { id: 'invert', x: buttonInset, y: cssHeight - buttonInset - size, size },
    { id: 'loop', x: cssWidth - buttonInset - size, y: buttonInset, size },
    { id: 'about', x: cssWidth - buttonInset - size, y: cssHeight - buttonInset - size, size },
  ];
}

export function drawButtons(
  ctx: CanvasRenderingContext2D,
  layout: Layout,
  state: EngineState,
  imgs: ButtonImages,
  hoverId: ButtonId | null,
): void {
  const rects = buttonRects(layout);
  ctx.save();
  for (const r of rects) {
    let img: HTMLImageElement | null = null;
    switch (r.id) {
      case 'linear':
        img = state.linear ? imgs.linear : imgs.random;
        break;
      case 'invert':
        img = state.invertPitch ? imgs.tempoHi : imgs.tempoLo;
        break;
      case 'loop':
        img = state.loop ? imgs.loop : imgs.noLoop;
        break;
      case 'about':
        img = imgs.about;
        break;
    }
    if (img && img.complete) {
      ctx.globalAlpha = 0.95;
      ctx.drawImage(img, r.x, r.y, r.size, r.size);
    } else {
      ctx.globalAlpha = 0.85;
      ctx.fillStyle = 'rgba(60,60,60,0.8)';
      ctx.fillRect(r.x, r.y, r.size, r.size);
    }
    if (hoverId === r.id) {
      ctx.globalAlpha = 1.0;
      ctx.strokeStyle = 'rgba(255,255,255,0.8)';
      ctx.lineWidth = 2;
      ctx.strokeRect(r.x + 1, r.y + 1, r.size - 2, r.size - 2);
    }
  }
  ctx.restore();
}

/**
 * Hit-test using iOS's full-height 12.5% strips (broader than the visible button),
 * matching original touch ergonomics. Returns null on grid area.
 */
export function hitTestButton(x: number, y: number, layout: Layout): ButtonId | null {
  const { zoneLeft, zoneRight, zoneSplit } = layout;
  if (x < zoneLeft) {
    return y < zoneSplit ? 'linear' : 'invert';
  }
  if (x > zoneRight) {
    return y < zoneSplit ? 'loop' : 'about';
  }
  return null;
}

export function hitTestVisibleButton(x: number, y: number, layout: Layout): ButtonId | null {
  for (const r of buttonRects(layout)) {
    if (x >= r.x && y >= r.y && x < r.x + r.size && y < r.y + r.size) return r.id;
  }
  return null;
}
